// Offline order queue — the web equivalent of Android's OfflineWorker/SaveOrderTable. LOCAL-FIRST
// (like the POS): a finalized (paid) order is persisted to IndexedDB and the receipt shows
// IMMEDIATELY; this worker syncs it to saveOrderPos in the background, retrying until the SERVER
// confirms. The cashier is never blocked on the API's response time, and an order is never lost to
// a saveOrderPos outage or a browser crash.
//
// Golden rules ported from the Android offline-sync architecture:
//  - Dequeue ONLY when body.status===true. HTTP 200 with {status:false} happens during outages;
//    treating HTTP-ok as "saved" caused real data loss.
//  - Per-order try/catch in the flush loop (an empty-body/parse error must not abort the whole queue).
//  - Retry-window cap: discard orders older than the window instead of retrying forever.

import { db } from "./db";
import { phpSaveOrder, auth } from "./api";

const RETRY_WINDOW_MS = 3 * 24 * 60 * 60 * 1000; // 3 days

// THE GOLDEN RULE — a queued order may be removed only when the server body says status===true.
export const isSaved = (res) => !!(res && (res.status === true || res.status === "true"));

// EXCEPTION (Android SaveOrderModel.isOrderAlreadyExists, OfflineCardFailWorker): saveOrderPos
// answers a duplicate order_id with {status:false, message:"Order already exists.", id:<server row>}
// — a PREVIOUS attempt saved it server-side and only the response was lost. It must count as a
// successful sync: otherwise the order retries every cycle forever ("↻ Syncing 1" for hours) and
// hammers the endpoint. Matched like Android: message contains "already exist", case-insensitive.
export const isOrderAlreadyExists = (res) =>
  !!(res && typeof res.message === "string" && res.message.toLowerCase().includes("already exist"));

// Notify listeners (the "↻ Syncing" badge) that the queue changed — keeps the count live instead of
// waiting for the next 20s poll.
function notifyQueueChanged() {
  try {
    if (typeof window !== "undefined") window.dispatchEvent(new Event("qv-offline-queue-changed"));
  } catch {
    /* non-browser (tests) */
  }
}

export async function enqueueOrder({ orderId, payload, kind = "cash", status = "paid", reconcileOrderId }) {
  if (!orderId) return;
  let merchantId = "";
  try {
    merchantId = (payload && payload.merchant_id) || auth().merchantId || "";
  } catch {
    /* auth unavailable — the payload's own merchant_id is what the server uses anyway */
  }
  await db.offlineOrders.put({
    orderId: String(orderId),
    merchantId, // informational scope; the FLUSH stays cross-merchant (payload is self-contained)
    payload,
    kind,
    status,
    reconcileOrderId: reconcileOrderId || null,
    createdAt: Date.now(),
    attempts: 0,
    lastError: "",
  });
  notifyQueueChanged();
}

export async function removeOfflineOrder(orderId) {
  await db.offlineOrders.delete(String(orderId));
  notifyQueueChanged();
}

export async function listOfflineOrders() {
  return db.offlineOrders.orderBy("createdAt").toArray();
}

// Pending-sale count for the "↻ Syncing" badge — PAID rows only. The offlineOrders table also holds
// "in_progress" crash-recovery snapshots (activeOrder.js); counting those made the badge show
// "Syncing 1" during every payment. ALL merchants' paid rows count: a queued sale from another store
// is still unsynced work this browser owns (the flush sends it regardless of the active store,
// because each payload carries its own merchant_id).
export async function countOfflineOrders() {
  try {
    return await db.offlineOrders.where("status").equals("paid").count();
  } catch {
    return 0;
  }
}

async function bump(o, error) {
  try {
    await db.offlineOrders.update(o.orderId, {
      attempts: (o.attempts || 0) + 1,
      lastError: String(error == null ? "" : error).slice(0, 300),
    });
  } catch {
    /* ignore */
  }
}

/**
 * Flush the queue once. Injectable `save`/`now` for tests. Returns { saved, kept, dropped }.
 * Each order is saved in its own try/catch so a single failure never skips the rest of the batch.
 */
export async function flushOfflineOrders({ save = phpSaveOrder, now = Date.now } = {}) {
  let saved = 0;
  let kept = 0;
  let dropped = 0;
  let orders;
  try {
    orders = await listOfflineOrders();
  } catch {
    return { saved, kept, dropped };
  }
  for (const o of orders) {
    if (o.status && o.status !== "paid") continue; // skip in-progress (unfinished) orders
    if (now() - (o.createdAt || 0) > RETRY_WINDOW_MS) {
      try {
        await removeOfflineOrder(o.orderId);
        dropped++;
      } catch {
        /* ignore */
      }
      continue;
    }
    try {
      const res = await save(o.payload);
      if (isSaved(res) || isOrderAlreadyExists(res)) {
        // already-exists = an earlier attempt committed server-side; safe (and required) to dequeue
        await removeOfflineOrder(o.orderId);
        saved++;
      } else {
        await bump(o, (res && (res.message || res.reason)) || "server rejected (status:false)");
        kept++;
      }
    } catch (e) {
      await bump(o, e && e.message ? e.message : String(e)); // keep + move to next (don't abort batch)
      kept++;
    }
  }
  notifyQueueChanged();
  return { saved, kept, dropped };
}

// SINGLE-FLIGHT background flush — the web mirror of Android's OfflineManager.startOfflineOperation()
// call right after the local insert. All callers (the 20s worker, online/visibility events, and the
// post-enqueue kick from submitRegisterOrder) share ONE in-flight run, so an order can never be
// double-POSTed by two overlapping flushes racing each other.
let _flushInFlight = null;

export function kickFlush() {
  if (_flushInFlight) return _flushInFlight;
  _flushInFlight = flushOfflineOrders()
    .catch(() => ({ saved: 0, kept: 0, dropped: 0 }))
    .finally(() => {
      _flushInFlight = null;
    });
  return _flushInFlight;
}
